// ============================================================================
// FNE-BRIDGE Frontend - Overrides des composants MUI
// ============================================================================

import type { Components, Theme } from '@mui/material/styles';

export const components: Components<Omit<Theme, 'components'>> = {
  MuiCssBaseline: {
    styleOverrides: (theme) => ({
      '*': {
        boxSizing: 'border-box',
      },
      html: {
        scrollBehavior: 'smooth',
      },
      body: {
        overflowX: 'hidden',
      },
      '::-webkit-scrollbar': {
        width: 8,
        height: 8,
      },
      '::-webkit-scrollbar-thumb': {
        backgroundColor: theme.palette.mode === 'dark' ? '#555555' : '#BDBDBD',
        borderRadius: 4,
      },
      '::-webkit-scrollbar-track': {
        backgroundColor: 'transparent',
      },
    }),
  },

  MuiButton: {
    defaultProps: {
      disableElevation: true,
    },
    styleOverrides: {
      root: {
        borderRadius: 8,
        padding: '8px 20px',
        fontWeight: 600,
        transition: 'all 0.2s ease-in-out',
      },
      sizeSmall: {
        padding: '4px 12px',
        fontSize: '0.8125rem',
      },
      sizeLarge: {
        padding: '12px 28px',
        fontSize: '1rem',
      },
      containedPrimary: {
        '&:hover': {
          transform: 'translateY(-1px)',
          boxShadow: '0 4px 12px rgba(27, 94, 32, 0.3)',
        },
      },
      outlined: {
        borderWidth: 2,
        '&:hover': {
          borderWidth: 2,
        },
      },
    },
  },

  MuiCard: {
    defaultProps: {
      elevation: 0,
    },
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: 12,
        border: `1px solid ${theme.palette.divider}`,
        transition: 'box-shadow 0.2s ease-in-out, transform 0.2s ease-in-out',
        '&:hover': {
          boxShadow:
            theme.palette.mode === 'dark'
              ? '0 4px 20px rgba(0, 0, 0, 0.4)'
              : '0 4px 20px rgba(0, 0, 0, 0.08)',
        },
      }),
    },
  },

  MuiTextField: {
    defaultProps: {
      size: 'small',
      variant: 'outlined',
    },
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          borderRadius: 8,
          transition: 'box-shadow 0.2s ease-in-out',
          '&.Mui-focused': {
            boxShadow: '0 0 0 3px rgba(27, 94, 32, 0.12)',
          },
        },
      },
    },
  },

  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: 6,
        fontWeight: 500,
      },
    },
  },

  MuiTableHead: {
    styleOverrides: {
      root: ({ theme }) => ({
        '& .MuiTableCell-head': {
          fontWeight: 700,
          fontSize: '0.75rem',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          backgroundColor: theme.palette.mode === 'dark' ? '#2A2A2A' : '#F5F7FA',
          color: theme.palette.mode === 'dark' ? '#B0B0B0' : '#616161',
        },
      }),
    },
  },

  MuiTableCell: {
    styleOverrides: {
      root: ({ theme }) => ({
        padding: '12px 16px',
        borderColor: theme.palette.divider,
      }),
    },
  },

  MuiTableRow: {
    styleOverrides: {
      root: ({ theme }) => ({
        '&:hover': {
          // Use theme.palette.action.hover so dark mode gets a subtle dark overlay
          // (white text stays readable) instead of a near-white hardcoded color.
          backgroundColor: `${theme.palette.action.hover} !important`,
        },
      }),
    },
  },

  MuiDialog: {
    styleOverrides: {
      paper: {
        borderRadius: 16,
      },
    },
  },

  MuiDialogTitle: {
    styleOverrides: {
      root: {
        fontSize: '1.125rem',
        fontWeight: 700,
        padding: '20px 24px 12px',
      },
    },
  },

  MuiAlert: {
    styleOverrides: {
      root: {
        borderRadius: 8,
      },
      standardSuccess: ({ theme }) =>
        theme.palette.mode === 'dark'
          ? { backgroundColor: 'rgba(76, 175, 80, 0.16)', color: '#A5D6A7' }
          : { backgroundColor: '#E8F5E9', color: '#1B5E20' },
      standardError: ({ theme }) =>
        theme.palette.mode === 'dark'
          ? { backgroundColor: 'rgba(244, 67, 54, 0.16)', color: '#EF9A9A' }
          : { backgroundColor: '#FFEBEE', color: '#C62828' },
      standardWarning: ({ theme }) =>
        theme.palette.mode === 'dark'
          ? { backgroundColor: 'rgba(255, 152, 0, 0.16)', color: '#FFCC80' }
          : { backgroundColor: '#FFF3E0', color: '#E65100' },
      standardInfo: ({ theme }) =>
        theme.palette.mode === 'dark'
          ? { backgroundColor: 'rgba(33, 150, 243, 0.16)', color: '#90CAF9' }
          : { backgroundColor: '#E3F2FD', color: '#01579B' },
    },
  },

  MuiTooltip: {
    defaultProps: {
      arrow: true,
    },
    styleOverrides: {
      tooltip: {
        borderRadius: 6,
        fontSize: '0.75rem',
      },
    },
  },

  MuiPaper: {
    defaultProps: {
      elevation: 0,
    },
    styleOverrides: {
      root: {
        backgroundImage: 'none',
      },
    },
  },

  MuiDrawer: {
    styleOverrides: {
      paper: {
        borderRight: 'none',
      },
    },
  },

  MuiLinearProgress: {
    styleOverrides: {
      root: {
        borderRadius: 4,
        height: 6,
      },
    },
  },

  MuiSkeleton: {
    styleOverrides: {
      root: {
        borderRadius: 8,
      },
    },
  },

  MuiBreadcrumbs: {
    styleOverrides: {
      root: {
        fontSize: '0.8125rem',
      },
    },
  },

  MuiTab: {
    styleOverrides: {
      root: {
        textTransform: 'none',
        fontWeight: 600,
        minHeight: 44,
      },
    },
  },
};
